import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BASE_URL from "BASE_URL";

import {
  Card,
  Grid,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  Box,
  TextField,
  Slide,
  Fade,
  Paper,
  Avatar,
  LinearProgress,
} from "@mui/material";
import { keyframes } from "@mui/system";

import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import BarChartIcon from "@mui/icons-material/BarChart";

// Premium Animations
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(76, 175, 80, 0.2); }
  50% { box-shadow: 0 0 20px rgba(76, 175, 80, 0.5); }
  100% { box-shadow: 0 0 5px rgba(76, 175, 80, 0.2); }
`;

function MetalPriceManagement() {
  const [metalPrices, setMetalPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Filter state
  const [filterMetal, setFilterMetal] = useState("");
  const [filterTrend, setFilterTrend] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  const token = localStorage.getItem("token");
  const metaGreen = "#2E7D32";
  const metaRed = "#C62828";
  const metaBlue = "#1565C0";

  // Fetch all metal prices
  const fetchMetalPrices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/metal-price/getAllMetalPrices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setMetalPrices(response.data.data);
      } else {
        showSnackbar(response.data.message || "Failed to fetch metal prices", "error");
      }
    } catch (error) {
      console.error("Error fetching metal prices:", error);
      showSnackbar(error.response?.data?.message || "Error fetching metal prices", "error");
    } finally {
      setLoading(false);
    }
  };

  // Refresh data
  const refreshData = async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(`${BASE_URL}/metal-price/getAllMetalPrices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setMetalPrices(response.data.data);
        showSnackbar("Metal prices refreshed successfully!", "success");
      }
    } catch (error) {
      console.error("Error refreshing metal prices:", error);
      showSnackbar("Failed to refresh metal prices", "error");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchMetalPrices();
  }, []);

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  // Format difference
  const formatDifference = (difference) => {
    const absDiff = Math.abs(difference);
    return `${difference > 0 ? "+" : "-"} ${formatPrice(absDiff)}`;
  };

  // Get trend chip
  const getTrendChip = (trend) => {
    if (trend === "up") {
      return (
        <Chip
          icon={<TrendingUpIcon />}
          label="UP"
          size="small"
          sx={{
            bgcolor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: "bold",
            "& .MuiChip-icon": { color: "#2e7d32" },
          }}
        />
      );
    } else if (trend === "down") {
      return (
        <Chip
          icon={<TrendingDownIcon />}
          label="DOWN"
          size="small"
          sx={{
            bgcolor: "#ffebee",
            color: "#c62828",
            fontWeight: "bold",
            "& .MuiChip-icon": { color: "#c62828" },
          }}
        />
      );
    }
    return <Chip label="STABLE" size="small" />;
  };

  // Get difference color
  const getDifferenceColor = (difference) => {
    return difference > 0 ? "#2e7d32" : difference < 0 ? "#c62828" : "#757575";
  };

  // Clear filters
  const clearFilters = () => {
    setFilterMetal("");
    setFilterTrend("");
    setFilterCategory("");
  };

  // Filtered metal prices
  const filteredMetalPrices = useMemo(() => {
    let filtered = [...metalPrices];

    if (filterMetal) {
      filtered = filtered.filter((item) => item.metal.toLowerCase() === filterMetal.toLowerCase());
    }

    if (filterCategory) {
      // Fix: Use exact matching instead of includes
      filtered = filtered.filter(
        (item) => item.category.toLowerCase() === filterCategory.toLowerCase()
      );
    }

    if (filterTrend) {
      filtered = filtered.filter((item) => item.trend === filterTrend);
    }

    return filtered;
  }, [metalPrices, filterMetal, filterTrend, filterCategory]);

  const columns = [
    // {
    //   Header: (
    //     <MDTypography variant="caption" fontWeight="bold">
    //       S.No
    //     </MDTypography>
    //   ),
    //   accessor: "sno",
    //   align: "center",
    //   width: "5%",
    // },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          METAL NAME
        </MDTypography>
      ),
      accessor: "metal",
      align: "left",
      //   width: "20%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          CATEGORY
        </MDTypography>
      ),
      accessor: "category",
      align: "center",
      //   width: "20%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          CURRENT PRICE
        </MDTypography>
      ),
      accessor: "current_price",
      align: "center",
      //   width: "20%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          OLD PRICE
        </MDTypography>
      ),
      accessor: "old_price",
      align: "center",
      //   width: "20%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          USD PRICE ($)
        </MDTypography>
      ),
      accessor: "usd_price",
      align: "center",
      //   width: "20%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          DIFFERENCE
        </MDTypography>
      ),
      accessor: "difference",
      align: "center",
      //   width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          TREND
        </MDTypography>
      ),
      accessor: "trend",
      align: "center",
      //   width: "10%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          UPDATED
        </MDTypography>
      ),
      accessor: "updatedAt",
      align: "center",
      //   width: "10%",
    },
  ];

  const rows = useMemo(
    () =>
      filteredMetalPrices.map((item, index) => ({
        sno: (
          <MDTypography variant="caption" fontWeight="medium" textAlign="center">
            {index + 1}
          </MDTypography>
        ),
        metal: (
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <Avatar
              sx={{
                width: 35,
                height: 35,
                bgcolor: item.trend === "up" ? metaGreen : metaRed,
                animation: `${float} 2s ease-in-out infinite`,
              }}
            >
              {item.trend === "up" ? <TrendingUpIcon /> : <TrendingDownIcon />}
            </Avatar>
            <MDTypography variant="button" fontWeight="bold" textTransform="capitalize">
              {item.metal}
            </MDTypography>
          </MDBox>
        ),
        category: (
          <MDTypography variant="caption" fontWeight="medium" sx={{ textTransform: "capitalize" }}>
            {item.category}
          </MDTypography>
        ),
        current_price: (
          <MDTypography variant="h6" fontWeight="bold" sx={{ color: metaBlue }}>
            {formatPrice(item.current_price)}
          </MDTypography>
        ),
        old_price: (
          <MDTypography variant="body2" color="text" fontWeight="medium">
            {formatPrice(item.old_price)}
          </MDTypography>
        ),
        usd_price: (
          <MDTypography variant="body2" color="text" fontWeight="medium">
            ${item.dollar_per_ton}
          </MDTypography>
        ),
        difference: (
          <MDTypography
            variant="body2"
            fontWeight="bold"
            sx={{ color: getDifferenceColor(item.difference) }}
          >
            {formatDifference(item.difference)}
          </MDTypography>
        ),
        trend: getTrendChip(item.trend),
        updatedAt: (
          <MDTypography variant="caption" fontWeight="medium">
            {new Date(item.updatedAt).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </MDTypography>
        ),
      })),
    [filteredMetalPrices]
  );

  // Statistics cards
  const totalMetals = metalPrices.length;
  const upTrendCount = metalPrices.filter((item) => item.trend === "up").length;
  const downTrendCount = metalPrices.filter((item) => item.trend === "down").length;
  const avgPrice =
    metalPrices.reduce((sum, item) => sum + item.current_price, 0) / totalMetals || 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3} sx={{ animation: `${fadeInUp} 0.5s ease-out` }}>
        {/* <Grid container spacing={3}> */}
        {/* Statistics Cards */}
        {/* <Grid item xs={12} md={3}>
            <Card
              sx={{
                borderRadius: "20px",
                p: 2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDTypography variant="caption" opacity={0.8}>
                    Total Metals
                  </MDTypography>
                  <MDTypography variant="h3" fontWeight="bold">
                    {totalMetals}
                  </MDTypography>
                </MDBox>
                <ShowChartIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </MDBox>
            </Card>
          </Grid> */}

        {/* <Grid item xs={12} md={3}>
            <Card
              sx={{
                borderRadius: "20px",
                p: 2,
                background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                color: "white",
              }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDTypography variant="caption" opacity={0.8}>
                    Up Trend
                  </MDTypography>
                  <MDTypography variant="h3" fontWeight="bold">
                    {upTrendCount}
                  </MDTypography>
                </MDBox>
                <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </MDBox>
            </Card>
          </Grid> */}

        {/* <Grid item xs={12} md={3}>
            <Card
              sx={{
                borderRadius: "20px",
                p: 2,
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                color: "white",
              }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDTypography variant="caption" opacity={0.8}>
                    Down Trend
                  </MDTypography>
                  <MDTypography variant="h3" fontWeight="bold">
                    {downTrendCount}
                  </MDTypography>
                </MDBox>
                <TrendingDownIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </MDBox>
            </Card>
          </Grid> */}

        {/* <Grid item xs={12} md={3}>
            <Card
              sx={{
                borderRadius: "20px",
                p: 2,
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                color: "white",
              }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDTypography variant="caption" opacity={0.8}>
                    Avg Price
                  </MDTypography>
                  <MDTypography variant="h5" fontWeight="bold">
                    {formatPrice(avgPrice)}
                  </MDTypography>
                </MDBox>
                <BarChartIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </MDBox>
            </Card>
          </Grid> */}
        {/* </Grid> */}

        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "24px" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="success"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <AttachMoneyIcon sx={{ color: "#fff", fontSize: 28 }} />
                  <MDTypography variant="h6" color="white">
                    Metal Price Management
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1}>
                  <Tooltip title="Refresh Prices">
                    <MDButton
                      variant="gradient"
                      color="dark"
                      startIcon={<RefreshIcon />}
                      onClick={refreshData}
                      disabled={refreshing}
                      sx={{ borderRadius: "12px", textTransform: "none" }}
                    >
                      {refreshing ? "Refreshing..." : "Refresh"}
                    </MDButton>
                  </Tooltip>
                </MDBox>
              </MDBox>

              {/* Filter Section */}
              <Box sx={{ px: 3, pt: 3 }}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Search by Metal Name"
                      value={filterMetal}
                      onChange={(e) => setFilterMetal(e.target.value)}
                      placeholder="Enter metal name..."
                      variant="outlined"
                      size="small"
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      fullWidth
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      variant="outlined"
                      size="small"
                      SelectProps={{ native: true }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    >
                      <option value="">All Categories</option>
                      <option value="gold">Gold</option>
                      <option value="silver">Silver</option>
                      <option value="non-ferrous">Non-Ferrous</option>
                      <option value="ferrous">Ferrous</option>
                      <option value="precious-metals">Precious Metals</option>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      fullWidth
                      value={filterTrend}
                      onChange={(e) => setFilterTrend(e.target.value)}
                      variant="outlined"
                      size="small"
                      SelectProps={{ native: true }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    >
                      <option value="">All Trends</option>
                      <option value="up">Up Trend</option>
                      <option value="down">Down Trend</option>
                      <option value="same">Stable</option>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={clearFilters}
                      startIcon={<ClearIcon />}
                      fullWidth
                      sx={{ borderRadius: "10px", height: "40px" }}
                    >
                      Clear
                    </MDButton>
                  </Grid>
                </Grid>
              </Box>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress color="success" />
                  </MDBox>
                ) : filteredMetalPrices.length === 0 ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <MDTypography variant="h6" color="dark" fontWeight="medium">
                      No metal prices found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Slide}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default MetalPriceManagement;
